/**
 * Decisions — Round 36 (Workforce v2 · Alexandrite, Chief of Staff)
 *
 * ONE screen for everything that is waiting on a person, from every agent:
 *   Queue          grouped tabs (Content · Campaigns · Payouts · Escalations · Policy),
 *                  three buttons per item (Approve / Edit / Reject), bulk approve
 *                  for headlines, keyboard shortcuts, phone-friendly cards.
 *   History        what was decided, by whom.
 *   Brief & costs  the Daily Brief card (what is emailed at 07:00 ET), Send now,
 *                  vendor spend this month, monthly caps, approval record.
 *
 * Approving presses the owning agent's own button on the server and can only
 * ever run once. Payout requests and ban appeals are link-only: they open
 * their own page.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  RefreshCw, Check, X, Pencil, ExternalLink, AlertTriangle, Inbox, Keyboard, RotateCw, Send, Mail, DollarSign, Clock,
} from 'lucide-react';

const SEVERITY_DOT = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-amber-400', info: 'bg-gray-300', low: 'bg-gray-300' };
const AGENT_COLORS = {
  SAPPHIRE: '#2563eb', PERIDOT: '#65a30d', ONYX: '#374151', OBSIDIAN: '#7c3aed', TOPAZ: '#d97706', QUARTZ: '#0891b2',
  DIAMOND: '#8b5cf6', ALEXANDRITE: '#0f766e', OPAL: '#14b8a6', CITRINE: '#ca8a04', GARNET: '#dc2626', AMETHYST: '#9333ea', JADE: '#059669', TOURMALINE: '#db2777',
};
const NEEDS_CONFIRM = { peridot_campaign: 'Send this push now?', onyx_violation: 'Apply this action to the member?', obsidian_alert: 'Apply this action to the wallet?', auto_flip: 'Let these post without your approval from now on?' };

const ago = (iso) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());

const PayloadPreview = ({ item }) => {
  const p = item.payload || {};
  if (item.type === 'news_item') {
    return (
      <div className="mt-2 text-sm text-gray-600 space-y-1">
        {p.summary && <p className="text-gray-500 line-clamp-2">{p.summary}</p>}
        <p><span className="text-gray-400">Caption:</span> {p.caption ? <span className="text-gray-800">{p.caption}</span> : <em className="text-gray-400">Sapphire picks one</em>}</p>
        {p.link && <a href={p.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">Read the story <ExternalLink className="w-3 h-3" /></a>}
      </div>
    );
  }
  if (item.type === 'peridot_campaign') {
    return (
      <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
        <p className="font-semibold text-gray-900">{p.push_title}</p>
        <p className="text-gray-600">{p.push_body}</p>
      </div>
    );
  }
  if (item.type === 'audience_push' || item.type === 'host_reminder') {
    // Round 42: exactly what would land on the phone
    return (
      <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
        <p className="font-semibold text-gray-900">{p.push_title}</p>
        <p className="text-gray-600">{p.push_body}</p>
        {item.type === 'audience_push' && <p className="text-xs text-gray-400 mt-1">{p.count} member{p.count === 1 ? '' : 's'} · {p.push_count} with push on</p>}
      </div>
    );
  }
  if (item.type === 'social_post') {
    // Round 48: the picture and the caption exactly as they would be published
    return (
      <div className="mt-2 flex gap-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
        {p.video_url ? (
          // Round 56: a Studio video — the player, with the poster frame until it plays
          <video src={`${API_BASE_URL}${p.video_url}`} poster={p.image_url ? `${API_BASE_URL}${p.image_url}` : undefined} controls preload="metadata" className="shrink-0 w-40 max-h-56 rounded-lg border bg-black object-contain" data-testid="decision-video" />
        ) : p.image_url && <a href={`${API_BASE_URL}${p.image_url}`} target="_blank" rel="noreferrer" className="shrink-0"><img src={`${API_BASE_URL}${p.image_url}`} alt="" loading="lazy" className="w-28 h-28 rounded-lg object-cover border" /></a>}
        <div className="min-w-0 text-sm">
          <p className="text-gray-800 whitespace-pre-wrap">{p.text}</p>
          <p className="text-xs text-gray-400 mt-1">{(p.platforms || []).join(' · ')}{p.x_text && p.x_text !== p.text ? ' · X gets a shorter cut' : ''}</p>
          {(p.warnings || []).length > 0 && <p className="text-xs text-amber-700 mt-1">Lapis notes: {p.warnings.join('; ')}</p>}
        </div>
      </div>
    );
  }
  if (item.type === 'schedule_post' && p.content) {
    return <p className="mt-2 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap">{p.content}</p>;
  }
  if (item.type === 'policy_change' && p.diff) {
    // Round 41: the policy diff, right in the queue
    return <pre className="mt-2 text-[11px] bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto max-h-64 whitespace-pre-wrap">{p.diff}</pre>;
  }
  if (item.type === 'lapis_blocked') {
    return (
      <div className="mt-2 text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-2">
        <p className="text-red-800"><span className="font-semibold">Blocked words:</span> {(p.blocked_terms || []).join(', ') || '—'}{p.fail_open ? ' · sent anyway (transactional email)' : ' · not sent'}</p>
        {p.excerpt && <p className="text-gray-600 mt-1 whitespace-pre-wrap line-clamp-4">{p.excerpt}</p>}
        {(p.warnings || []).length > 0 && <p className="text-amber-700 mt-1 text-xs">Also flagged: {p.warnings.map((w) => `${w.rule} (“${w.match}”)`).join('; ')}</p>}
      </div>
    );
  }
  if (item.type === 'escalation' && p.suggested_action) {
    // Round 39: what a person should do about it, right in the queue
    return <p className="mt-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2"><span className="text-emerald-600 font-semibold">Suggested: </span>{p.suggested_action}</p>;
  }
  if (item.type === 'support_ticket' && p.topaz_said) {
    return <p className="mt-2 text-xs text-gray-500"><span className="text-gray-400">Topaz already said:</span> {p.topaz_said}</p>;
  }
  if (item.type === 'investor_update') {
    const m = p.metrics || {};
    return (
      <div className="mt-2 text-xs text-gray-600 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
        {Object.entries(m).map(([k, v]) => <span key={k}><span className="text-gray-400">{k.replace(/_/g, ' ')}:</span> {num(v)}</span>)}
      </div>
    );
  }
  return null;
};

const EditForm = ({ item, busy, onCancel, onSubmit }) => {
  const [values, setValues] = useState(() => {
    const v = {};
    (item.edit_fields || []).forEach((f) => { v[f.key] = (item.payload || {})[f.key] ?? (f.options ? f.options[0] : ''); });
    return v;
  });
  const set = (k, val) => setValues((prev) => ({ ...prev, [k]: val }));
  return (
    <div className="mt-3 border-t pt-3 space-y-3" data-testid={`decision-edit-${item.id}`}>
      {(item.edit_fields || []).map((f) => (
        <div key={f.key}>
          <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}{f.required ? ' *' : ''}</label>
          {f.options ? (
            <select value={values[f.key]} onChange={(e) => set(f.key, e.target.value)} className="w-full sm:w-64 px-3 py-2 border rounded-lg text-sm bg-white">
              {f.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
            </select>
          ) : f.multiline ? (
            <textarea value={values[f.key]} onChange={(e) => set(f.key, e.target.value)} maxLength={f.max || 500} rows={f.key === 'reply' ? 5 : 3}
              className="w-full px-3 py-2 border rounded-lg text-sm" autoFocus={f.key === 'reply'} />
          ) : (
            <input value={values[f.key]} onChange={(e) => set(f.key, e.target.value)} maxLength={f.max || 500} className="w-full px-3 py-2 border rounded-lg text-sm" />
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={() => onSubmit(values)} disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
          <Check className="w-4 h-4" /> {item.type === 'support_ticket' ? 'Send reply' : 'Save & approve'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );
};

const BriefCard = ({ brief }) => {
  if (!brief) return null;
  const n = brief.numbers || {};
  const tiles = [
    ['New members', num(n.new_users)], ['Active (24h)', `${num(n.dau)} / ${num(n.members)}`],
    ['Tables hosted', `${num(n.tables_hosted)} · avg ${num(n.avg_viewers)}`], ['Gifts sent', `${num(n.gifts_sent?.emeralds)} Emeralds`],
    ['Gifts received', `${num(n.gifts_received?.rubies)} Rubies`], ['Emerald purchases', `${num(n.emerald_purchases?.emeralds)} · $${Number(n.emerald_purchases?.usd || 0).toFixed(2)}`],
    ['Rubies paid out', `${num(n.rubies_paid?.rubies)} · $${Number(n.rubies_paid?.usd || 0).toFixed(2)}`],
    ['Store reviews', n.reviews?.count ? `${n.reviews.count} · ${n.reviews.avg}★` : '—'], ['Vendor spend (month)', `$${Number(n.vendor_spend?.total || 0).toFixed(2)}`],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {tiles.map(([l, v]) => (
          <div key={l} className="bg-white border rounded-lg p-3">
            <p className="text-[11px] text-gray-500">{l}</p>
            <p className="text-sm font-semibold text-gray-900">{v}</p>
          </div>
        ))}
      </div>
      {(n.signups_by_code || []).length > 0 && (
        <p className="text-xs text-gray-500">Signups by code: {n.signups_by_code.map((c) => `${c.code} (@${c.handle}) ×${c.count}`).join(', ')}</p>
      )}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b text-sm font-semibold text-gray-700">Your team</div>
        <div className="divide-y">
          {(brief.agents || []).map((a) => (
            <div key={a.agent} className="px-4 py-2 flex items-start gap-3 text-sm">
              <span className="w-24 shrink-0 font-medium" style={{ color: AGENT_COLORS[a.agent] || '#374151' }}>{a.agent.charAt(0) + a.agent.slice(1).toLowerCase()}</span>
              <span className="text-gray-700">{a.line}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b text-sm font-semibold text-gray-700">Incidents (24h)</div>
        {(brief.incidents || []).length === 0 ? <p className="px-4 py-3 text-sm text-gray-400">None.</p> : (
          <div className="divide-y">
            {brief.incidents.map((i) => (
              <div key={i.id} className="px-4 py-2 text-sm flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${SEVERITY_DOT[i.severity] || 'bg-gray-300'}`} />
                <span className="text-gray-900">{i.title}</span>
                <span className="text-xs text-gray-400">— {i.agent}, {i.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BriefAndCosts = ({ api }) => {
  const [brief, setBrief] = useState(null);
  const [costs, setCosts] = useState(null);
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState({ rates: {}, caps: {} });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [b, c, s] = await Promise.allSettled([api.get('/api/admin/decisions/brief'), api.get('/api/admin/decisions/costs'), api.get('/api/admin/decisions/stats')]);
    if (b.status === 'fulfilled') setBrief(b.value.data);
    if (c.status === 'fulfilled') setCosts(c.value.data);
    if (s.status === 'fulfilled') setStats(s.value.data);
    if ([b, c, s].some((x) => x.status === 'rejected')) toast.error('Some of this page could not load');
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    if (!window.confirm('Email the brief to every admin now?')) return;
    setSending(true);
    try {
      const r = await api.post('/api/admin/decisions/brief/send');
      const m = r.data?.email || {};
      if (m.skipped) toast.error(m.skipped); else toast.success(`Brief emailed to ${m.sent} of ${m.recipients}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send the brief'); }
    finally { setSending(false); }
  };

  const saveCosts = async () => {
    setSaving(true);
    try {
      const r = await api.put('/api/admin/decisions/costs', draft);
      setCosts(r.data); setDraft({ rates: {}, caps: {} });
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const shown = brief?.live;
  const rates = costs?.settings?.rates || {};
  const dirty = Object.keys(draft.rates).length + Object.keys(draft.caps).length > 0;
  return (
    <div className="space-y-6" data-testid="decisions-brief">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Daily Brief</h2>
          <p className="text-xs text-gray-500">
            Emailed to the admins at {brief?.schedule?.hour_et ?? 7}:00 ET while Alexandrite is running
            {brief && !brief.agent_running && <span className="text-amber-700"> — Alexandrite is stopped: start it in AI Workforce to get the email</span>}
            {brief?.latest && <> · last sent {brief.latest.date_et}: {brief.latest.email?.skipped || `${brief.latest.email?.sent ?? 0} email(s)`}</>}
          </p>
        </div>
        <button onClick={sendNow} disabled={sending} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50" data-testid="brief-send-now">
          <Mail className="w-4 h-4" /> {sending ? 'Sending…' : 'Email it now'}
        </button>
      </div>
      {shown ? <BriefCard brief={shown} /> : <div className="h-32 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>}

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700 inline-flex items-center gap-1.5"><DollarSign className="w-4 h-4" /> Vendor costs — {costs?.month_to_date?.month || 'this month'}: ${Number(costs?.month_to_date?.total || 0).toFixed(2)}</span>
          {dirty && <button onClick={saveCosts} disabled={saving} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium disabled:opacity-50">Save</button>}
        </div>
        <div className="p-4 grid md:grid-cols-2 gap-6 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Rates (estimates — USD per unit)</p>
            <div className="space-y-1.5">
              {Object.entries(rates).map(([vendor, r]) => (
                <div key={vendor} className="flex items-center gap-2">
                  <span className="flex-1 text-gray-700">{r.label || vendor} <span className="text-gray-400 text-xs">/ {String(r.unit).replace(/_/g, ' ')}</span></span>
                  <input type="number" step="0.0001" min="0" defaultValue={r.unit_rate} onChange={(e) => setDraft((d) => ({ ...d, rates: { ...d.rates, [vendor]: { unit_rate: e.target.value } } }))}
                    className="w-28 px-2 py-1 border rounded text-right text-sm" aria-label={`${vendor} rate`} />
                  <span className="w-16 text-right text-xs text-gray-500">${Number(costs?.month_to_date?.by_vendor?.[vendor] || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Monthly caps per agent (pauses at 90%, stops at 100%; 0 = no cap)</p>
            <div className="space-y-1.5">
              {(costs?.agents || []).map((a) => (
                <div key={a.agent} className="flex items-center gap-2">
                  <span className="flex-1 text-gray-700">{a.agent.charAt(0) + a.agent.slice(1).toLowerCase()}
                    <span className={`ml-2 text-xs ${a.state === 'ok' ? 'text-gray-400' : 'text-red-600 font-medium'}`}>${Number(a.spent).toFixed(2)} spent{a.state !== 'ok' ? ` — ${a.state}` : ''}</span>
                  </span>
                  <span className="text-gray-400">$</span>
                  <input type="number" step="1" min="0" defaultValue={a.cap} onChange={(e) => setDraft((d) => ({ ...d, caps: { ...d.caps, [a.agent]: e.target.value } }))}
                    className="w-24 px-2 py-1 border rounded text-right text-sm" aria-label={`${a.agent} cap`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b text-sm font-semibold text-gray-700">
          Approval record (last {stats?.days || 90} days) — auto is proposed at {stats?.auto_flip_rule?.min_decisions || 50}+ decisions with {Math.round((stats?.auto_flip_rule?.min_rate || 0.95) * 100)}% approved unchanged
        </div>
        {(stats?.stats || []).length === 0 ? <p className="px-4 py-3 text-sm text-gray-400">No decisions made here yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 border-b">
                <th className="px-4 py-2">Agent</th><th className="px-2 py-2">What</th><th className="px-2 py-2">Channel</th>
                <th className="px-2 py-2 text-right">Decisions</th><th className="px-2 py-2 text-right">Unchanged</th><th className="px-2 py-2 text-right">Edited</th><th className="px-4 py-2 text-right">Rejected</th>
              </tr></thead>
              <tbody className="divide-y">
                {stats.stats.map((s) => (
                  <tr key={`${s.agent}-${s.type}-${s.channel}`}>
                    <td className="px-4 py-2 font-medium" style={{ color: AGENT_COLORS[s.agent] || '#374151' }}>{s.agent}</td>
                    <td className="px-2 py-2 text-gray-700">{String(s.type).replace(/_/g, ' ')}</td><td className="px-2 py-2 text-gray-500">{s.channel || '—'}</td>
                    <td className="px-2 py-2 text-right">{s.total}</td><td className="px-2 py-2 text-right">{Math.round(s.rate * 100)}%</td>
                    <td className="px-2 py-2 text-right">{s.edited}</td><td className="px-4 py-2 text-right">{s.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const DecisionQueuePage = () => {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const group = params.get('group') || '';
  const view = params.get('view') || 'queue';           // queue | history | brief
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ by_group: {}, total: 0, failed: 0 });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState('');
  const [selected, setSelected] = useState({});
  const [focus, setFocus] = useState(0);
  const [showKeys, setShowKeys] = useState(false);
  const cardRefs = useRef({});

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (view === 'brief') {
      api.get('/api/admin/decisions/counts').then((r) => setCounts(r.data || { by_group: {}, total: 0, failed: 0 })).catch(() => {});
      setLoading(false);
      return;
    }
    try {
      const r = await api.get('/api/admin/decisions', { params: { group: group || undefined, status: view === 'history' ? 'history' : 'open', limit: 150 }, timeout: 20000 });
      setItems(r.data.items || []); setCounts(r.data.counts || { by_group: {}, total: 0, failed: 0 }); setGroups(r.data.groups || []);
      setError('');
    } catch (e) { setError(e.response?.data?.detail || 'Could not load the queue'); }
    finally { setLoading(false); }
  }, [api, group, view]);

  useEffect(() => { setLoading(true); setSelected({}); setEditing(''); setFocus(0); load(); }, [load]);
  useEffect(() => { const t = setInterval(() => { if (!editing && !busy) load(); }, 45000); return () => clearInterval(t); }, [load, editing, busy]);

  const decide = useCallback(async (item, action, edited) => {
    if (action === 'approve' && NEEDS_CONFIRM[item.type] && !window.confirm(`${NEEDS_CONFIRM[item.type]}\n\n${item.title}`)) return;
    setBusy(item.id);
    try {
      const r = await api.post(`/api/admin/decisions/${item.id}/decide`, { action, edited_payload: edited || null });
      if (r.data?.counts) setCounts(r.data.counts);
      if (r.data?.ok) {
        toast.success(action === 'reject' ? (item.labels?.reject || 'Rejected') : r.data.item?.result?.note || r.data.item?.result?.applied || 'Done');
        setItems((prev) => prev.filter((x) => x.id !== item.id)); setEditing('');
      } else if (r.data?.status === 'pending') {
        toast.error(r.data.error || 'Check what you typed');
      } else {
        toast.error(r.data?.error || 'Could not complete that'); load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setBusy(''); }
  }, [api, load]);

  const retry = async (item) => {
    try { await api.post(`/api/admin/decisions/${item.id}/retry`); toast.success('Back in the queue'); load(); } catch { toast.error('Could not retry'); }
  };

  const bulkIds = useMemo(() => Object.keys(selected).filter((id) => selected[id]), [selected]);
  const bulk = useCallback(async (action) => {
    if (!bulkIds.length) return;
    if (!window.confirm(`${action === 'approve' ? 'Approve' : 'Reject'} ${bulkIds.length} item${bulkIds.length === 1 ? '' : 's'}?`)) return;
    setBusy('bulk');
    try {
      const r = await api.post('/api/admin/decisions/bulk', { ids: bulkIds, action });
      toast.success(`${r.data.done} done${r.data.failed?.length ? `, ${r.data.failed.length} failed` : ''}${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`);
      setSelected({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
    finally { setBusy(''); }
  }, [api, bulkIds, load]);

  // keyboard: j/k move · a approve · e edit · r reject · x select · o open · Shift+A approve selected · ? help
  useEffect(() => {
    if (view !== 'queue') return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.metaKey || e.ctrlKey || e.altKey) return;
      const item = items[focus];
      const move = (d) => { const n = Math.max(0, Math.min(items.length - 1, focus + d)); setFocus(n); cardRefs.current[items[n]?.id]?.scrollIntoView({ block: 'nearest' }); };
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === '?') setShowKeys((s) => !s);
      else if (e.key === 'A' && e.shiftKey) bulk('approve');
      else if (!item || busy) return;
      else if (e.key === 'a' && item.actions.includes('approve') && item.status === 'pending') decide(item, 'approve');
      else if (e.key === 'r' && item.actions.includes('reject') && item.status === 'pending') decide(item, 'reject');
      else if (e.key === 'e' && item.actions.includes('edit') && item.status === 'pending') { e.preventDefault(); setEditing(item.id); }
      else if (e.key === 'x' && item.bulk_ok) setSelected((s) => ({ ...s, [item.id]: !s[item.id] }));
      else if (e.key === 'o' && item.link) navigate(item.link);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, items, focus, busy, decide, bulk, navigate]);

  const bulkable = items.filter((i) => i.bulk_ok && i.status === 'pending');
  const tabs = [{ key: '', label: 'All', n: counts.total }, ...groups.map((g) => ({ key: g.key, label: g.label, n: counts.by_group?.[g.key] || 0 }))];

  return (
    <div className="space-y-4 max-w-5xl" data-testid="decisions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Decisions</h1>
          <p className="text-sm text-gray-500">Everything waiting on you, from every agent — {counts.total} open{counts.failed ? `, ${counts.failed} failed` : ''}.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowKeys((s) => !s)} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50" aria-label="Keyboard shortcuts"><Keyboard className="w-4 h-4" /> Keys</button>
          <button onClick={() => { setLoading(true); load(); }} className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      <div className="flex gap-1 border-b overflow-x-auto">
        {[['queue', 'Queue'], ['history', 'History'], ['brief', 'Brief & costs']].map(([k, l]) => (
          <button key={k} onClick={() => setParam({ view: k === 'queue' ? '' : k })} data-testid={`decisions-view-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${view === k ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>

      {showKeys && view === 'queue' && (
        <div className="bg-gray-900 text-gray-100 rounded-xl px-4 py-3 text-xs grid grid-cols-2 sm:grid-cols-4 gap-y-1.5">
          {[['j / k', 'next / previous'], ['a', 'approve'], ['e', 'edit'], ['r', 'reject'], ['x', 'select (headlines)'], ['Shift + A', 'approve selected'], ['o', 'open its page'], ['?', 'this help']].map(([k, d]) => (
            <span key={k}><kbd className="px-1.5 py-0.5 rounded bg-gray-700 font-mono">{k}</kbd> <span className="text-gray-400">{d}</span></span>
          ))}
        </div>
      )}

      {view === 'brief' ? <BriefAndCosts api={api} /> : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {tabs.map((t) => (
              <button key={t.key || 'all'} onClick={() => setParam({ group: t.key })} data-testid={`decisions-group-${t.key || 'all'}`}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border ${group === t.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                {t.label}{view === 'queue' && <span className={`ml-1.5 text-xs ${group === t.key ? 'text-gray-300' : 'text-gray-400'}`}>{t.n}</span>}
              </button>
            ))}
          </div>

          {view === 'queue' && bulkable.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm bg-white border rounded-xl px-3 py-2 sticky top-0 z-10">
              <button onClick={() => setSelected(bulkIds.length === bulkable.length ? {} : Object.fromEntries(bulkable.map((i) => [i.id, true])))} className="px-3 py-1.5 border rounded-lg hover:bg-gray-50">
                {bulkIds.length === bulkable.length ? 'Clear' : `Select all ${bulkable.length} headlines`}
              </button>
              {bulkIds.length > 0 && (
                <>
                  <button onClick={() => bulk('approve')} disabled={busy === 'bulk'} data-testid="decisions-bulk-approve"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"><Check className="w-4 h-4" /> Approve {bulkIds.length}</button>
                  <button onClick={() => bulk('reject')} disabled={busy === 'bulk'} className="px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50">Reject {bulkIds.length}</button>
                </>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
              <span>{error}</span><button onClick={load} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium">Retry</button>
            </div>
          )}

          {loading ? <div className="h-48 flex items-center justify-center"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div> : items.length === 0 ? (
            <div className="bg-white border rounded-xl py-14 text-center text-gray-400" data-testid="decisions-empty">
              <Inbox className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">{view === 'history' ? 'Nothing decided here yet.' : 'Nothing is waiting on you. The agents have it.'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item, idx) => {
                const open = item.status === 'pending';
                return (
                  <div key={item.id} ref={(el) => { cardRefs.current[item.id] = el; }} onClick={() => setFocus(idx)} data-testid={`decision-${item.type}`}
                    className={`bg-white border rounded-xl p-4 ${view === 'queue' && idx === focus ? 'ring-2 ring-gray-900/70' : ''} ${item.status === 'failed' ? 'border-red-300' : ''}`}>
                    <div className="flex items-start gap-3">
                      {view === 'queue' && item.bulk_ok && open && (
                        <input type="checkbox" checked={!!selected[item.id]} onChange={(e) => setSelected((s) => ({ ...s, [item.id]: e.target.checked }))}
                          className="mt-1 w-4 h-4 rounded border-gray-300" aria-label={`Select ${item.title}`} />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className={`w-2 h-2 rounded-full ${SEVERITY_DOT[item.severity] || 'bg-gray-300'}`} />
                          <span className="font-semibold" style={{ color: AGENT_COLORS[item.agent] || '#374151' }}>{item.agent}</span>
                          <span className="text-gray-400">{item.group_label}</span>
                          <span className="text-gray-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{ago(view === 'history' ? item.decided_at : item.created_at)}</span>
                          {item.expires_at && open && <span className="text-amber-700">expires {new Date(item.expires_at).toLocaleDateString()}</span>}
                          {view === 'history' && <span className={`px-1.5 py-0.5 rounded font-medium ${item.status === 'rejected' ? 'bg-red-50 text-red-700' : item.status === 'approved' || item.status === 'edited' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                            {item.status}{item.decided_by_name ? ` · ${item.decided_by_name}` : item.decided_via === 'source' ? ' · in its own page' : ''}</span>}
                          {view === 'history' && item.result?.note && <span className="text-gray-500 italic">{item.result.note}</span>}
                        </div>
                        <p className="mt-1 font-medium text-gray-900 break-words">{item.title}</p>
                        {item.summary && <p className="text-sm text-gray-600 break-words">{item.summary}</p>}
                        <PayloadPreview item={item} />
                        {item.status === 'failed' && (
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            <AlertTriangle className="w-4 h-4 shrink-0" /><span className="flex-1 min-w-0 break-words">It did not go through: {item.error}</span>
                            <button onClick={() => retry(item)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-red-600 text-white text-xs font-medium"><RotateCw className="w-3 h-3" /> Retry</button>
                          </div>
                        )}
                        {view === 'queue' && open && editing !== item.id && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {item.actions.includes('approve') && (
                              <button onClick={() => decide(item, 'approve')} disabled={!!busy} data-testid="decision-approve"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                                {item.type === 'peridot_campaign' ? <Send className="w-4 h-4" /> : <Check className="w-4 h-4" />} {item.labels?.approve || 'Approve'}
                              </button>
                            )}
                            {item.actions.includes('edit') && (
                              <button onClick={() => setEditing(item.id)} disabled={!!busy} data-testid="decision-edit"
                                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${item.actions.includes('approve') ? 'border hover:bg-gray-50' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
                                <Pencil className="w-4 h-4" /> {item.labels?.edit || 'Edit'}
                              </button>
                            )}
                            {item.actions.includes('reject') && (
                              <button onClick={() => decide(item, 'reject')} disabled={!!busy} data-testid="decision-reject"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50 disabled:opacity-50">
                                <X className="w-4 h-4" /> {item.labels?.reject || 'Reject'}
                              </button>
                            )}
                            {item.link && (
                              <button onClick={() => navigate(item.link)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">
                                <ExternalLink className="w-4 h-4" /> {item.labels?.open || 'Open its page'}
                              </button>
                            )}
                          </div>
                        )}
                        {editing === item.id && <EditForm item={item} busy={busy === item.id} onCancel={() => setEditing('')} onSubmit={(v) => decide(item, 'edit', v)} />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DecisionQueuePage;
